import { ReactComponent as ExcelSvg } from "assets/icon_excel.svg";
import Button from "components/button/button";
import Modal from "components/modal/modal";
import { AppAction, AppContext } from "helpers/context";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import * as XLSX from "xlsx";
// import { v4 as uuidv4 } from "uuid";
import countryArray from "assets/json/country.json";
import { getLanguage } from "helpers/translations";
import styles from "./import-modal.module.scss";
import { OCCUPANCY } from "helpers/constants";

function ImportModal() {
  const [context, dispatch] = useContext(AppContext);
  const [t] = useTranslation();
  const [loading, setLoading] = useState(false);
  const language = getLanguage();

  const close = (): void => {
    dispatch({ type: AppAction.setImportModal, payload: false });
  };

  const isValidDate = (dateString: string) => {
    if (!/^\d{4}-\d{1,2}-\d{1,2}$/.test(dateString)) return false;
    const date = new Date(dateString);
    return date instanceof Date && !isNaN(date.getTime());
  };

  const formatDate = (date: Date | string | number): string => {
    if (date instanceof Date) {
      date.setDate(date.getDate() + 1);
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, "0");
      const day = date.getDate().toString().padStart(2, "0");
      return `${year}-${month}-${day}`;
    }
    if (typeof date === "string") {
      return date.replace(/\//g, "-");
    }
    return "";
  };

  const hanldeChangeUploadFile = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    setLoading(true);
    const locationSource = context.locationSource;
    if (!locationSource) {
      return;
    }

    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    const reader = new FileReader();

    const errorData: { row: number; message: string }[] = [];

    reader.onloadend = (progressEvent) => {
      const arrayBuffer = reader.result;
      const options: XLSX.ParsingOptions = { type: "array", cellDates: true };
      const workbook = XLSX.read(arrayBuffer, options);
      const sheetName = workbook.SheetNames;
      const sheet = workbook.Sheets[sheetName[0]];
      const sheet1 = workbook.Sheets[sheetName[1]];
      const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 1 });
      const jsonData1 = XLSX.utils.sheet_to_json(sheet1);
      const headerData = XLSX.utils.sheet_to_json(sheet1, {
        header: 1,
        range: 0
      })[0];

      if (!headerData) {
        dispatch({
          type: AppAction.addSnackBarParams,
          payload: {
            title: "invalidTemplate",
            titleIconColor: "yellow",
            titleIcon: "warning",
            message: "emptySheet"
          }
        });
        setLoading(false);
        close();
        return;
      }

      if (!Array.isArray(headerData) || headerData.length === 0) {
        dispatch({
          type: AppAction.addSnackBarParams,
          payload: {
            title: "invalidTemplate",
            titleIconColor: "yellow",
            titleIcon: "warning",
            message: "invalidText"
          }
        });
        setLoading(false);
        close();
        return;
      }

      const tArray = [t("importLocationSet.id"), t("importLocationSet.name")];
      let flag = true;
      tArray.forEach((item) => {
        if (!headerData.includes(item)) {
          flag = false;
          return;
        }
      });

      if (!flag) {
        dispatch({
          type: AppAction.addSnackBarParams,
          payload: {
            title: "WrongTemplate",
            titleIconColor: "yellow",
            titleIcon: "warning",
            message: "WrongTemplateText"
          }
        });
        setLoading(false);
        close();
        return;
      }

      if (!jsonData || jsonData.length === 0) {
        dispatch({
          type: AppAction.addSnackBarParams,
          payload: {
            title: "emptyTitle",
            titleIconColor: "yellow",
            titleIcon: "warning",
            message: "emptyText"
          }
        });
        setLoading(false);
        close();
        return;
      }
      const headerRows = jsonData.filter(
        (row: any) =>
          Array.isArray(row) &&
          row.some(
            (cell: any) => cell !== undefined && cell !== null && cell !== ""
          )
      );
      const header = headerRows[0];
      const headerCurrency = headerRows[1];
      const headerComment = headerRows[2];
      if (
        !header ||
        !Array.isArray(header) ||
        header.length === 0 ||
        !headerCurrency ||
        !Array.isArray(headerCurrency) ||
        headerCurrency.length === 0 ||
        !headerComment ||
        !Array.isArray(headerComment) ||
        headerComment.length === 0
      ) {
        dispatch({
          type: AppAction.addSnackBarParams,
          payload: {
            title: "emptyTitle",
            titleIconColor: "yellow",
            titleIcon: "warning",
            message: "emptyText"
          }
        });
        setLoading(false);
        close();
        return;
      }

      const name = header[1];
      const currency = headerCurrency[1];
      const comment = headerComment[1];

      if (!Array.isArray(headerRows[0]) || headerRows[0].length === 0) {
        dispatch({
          type: AppAction.addSnackBarParams,
          payload: {
            title: "emptyTitle",
            // text: "ccc",
            titleIconColor: "yellow",
            titleIcon: "warning",
            message: "emptyText"
          }
        });
        setLoading(false);
        close();
        return;
      }

      const ids = jsonData1.map((e: any) => e[t("importLocationSet.id")]);
      const uniqueIds = Array.from(new Set(ids));
      if (ids.length !== uniqueIds.length) {
        dispatch({
          type: AppAction.addSnackBarParams,
          payload: {
            title: "duplicateIdTitle",
            titleIconColor: "yellow",
            titleIcon: "warning",
            message: "duplicateIdText"
          }
        });
        setLoading(false);
        close();
        return;
      }

      // const newUUID = uuidv4();

      const items = jsonData1.map((e: any, i) => {
        const row = i + 2;

        const code = e[t("importLocationSet.id")];
        const name = e[t("importLocationSet.name")];

        if (!code) {
          errorData.push({
            row,
            message: t("importErrorMessage.idError")
          });
        }

        if (!name) {
          errorData.push({
            row,
            message: t("importErrorMessage.nameError")
          });
        }

        const latitude = e[t("importLocationSet.latitude")];
        const longitude = e[t("importLocationSet.longitude")];

        const country = e[t("importLocationSet.country")] || "";

        const street = e[t("importLocationSet.street")] || "";
        const place = (e[t("importLocationSet.place")] || "").replace(
          "undefined",
          ""
        );
        const province = e[t("importLocationSet.province")] || "";
        const zipCode = (e[t("importLocationSet.zipCode")] + "").replace(
          "undefined",
          ""
        );

        const coordinateSystem = e[t("importLocationSet.coordinateSource")];
        const coordinateSource =
          locationSource === "latlng" ? "userSupplied" : "geoEncoded";

        if (locationSource === "latlng") {
          if (!coordinateSystem) {
            errorData.push({
              row,
              message: t("importErrorMessage.coordinateError")
            });
          } else if (
            coordinateSystem !== "GCJ02" &&
            coordinateSystem !== "WGS84"
          ) {
            errorData.push({
              row,
              message: t("importErrorMessage.coordinateRangeError")
            });
          }

          if (!latitude) {
            errorData.push({
              row,
              message: t("importErrorMessage.latitudeError")
            });
          } else if (latitude < -90 || latitude > 90) {
            errorData.push({
              row,
              message: t("importErrorMessage.latitudeRangeError")
            });
          }

          if (!longitude) {
            errorData.push({
              row,
              message: t("importErrorMessage.longitudeError")
            });
          } else if (longitude < -180 || longitude > 180) {
            errorData.push({
              row,
              message: t("importErrorMessage.longitudeRangeError")
            });
          }
        } else if (locationSource === "address") {
          if (!country) {
            errorData.push({
              row,
              message: t("importErrorMessage.countryError")
            });
          }

          if (!street && !place && !province && !zipCode) {
            errorData.push({
              row,
              message: t("importErrorMessage.addressError")
            });
          }
        }

        let countryISO3 = e[t("importLocationSet.countryISO3")];

        if (!countryISO3 && country) {
          const countryItem = countryArray.filter((item) =>
            item.label.toLowerCase().includes(country.toLowerCase())
          )[0];
          if (countryItem) {
            countryISO3 = countryItem.iso3;
          }
        }

        const number = (e[t("importLocationSet.number")] + "").replace(
          "undefined",
          ""
        );

        const county = e[t("importLocationSet.county")];

        const addressEn = [street, number, county, place, province, country]
          .filter(Boolean)
          .join(", ");

        const addressCn = `${country || ""} ${province || ""} ${place || ""} ${county || ""} ${street || ""} ${number || ""}`;

        const address = (language === "en" ? addressEn : addressCn).replace(
          "undefined",
          ""
        );

        const insuredName = e[t("locations.add.insuredName")] || "";
        const insuredCode = e[t("locations.add.insuredCode")] || "";
        const inceptionDateRaw = e[t("locations.add.inceptionDate")] || "";
        const expirationDateRaw = e[t("locations.add.expirationDate")] || "";
        const evaluationDateRaw = e[t("locations.add.evaluationDate")] || "";

        const inceptionDate = formatDate(inceptionDateRaw);
        const expirationDate = formatDate(expirationDateRaw);
        const evaluationDate = formatDate(evaluationDateRaw);

        if (inceptionDate && !isValidDate(inceptionDate)) {
          errorData.push({
            row,
            message: t("importErrorMessage.inceptionDateError")
          });
        }
        if (expirationDate && !isValidDate(expirationDate)) {
          errorData.push({
            row,
            message: t("importErrorMessage.expirationDateError")
          });
        }
        if (evaluationDate && !isValidDate(evaluationDate)) {
          errorData.push({
            row,
            message: t("importErrorMessage.evaluationDateError")
          });
        }

        // const currency = e[t("locations.add.currency")] || null;
        const perilsCovered = e[t("locations.add.perilsCovered")] || null;
        const occupancyLabel: string = e[t("locations.add.occupancy")] || null;
        const numberOfRisks = e[t("locations.add.numberOfRisks")] || null;
        const building = e[t("locations.add.building")] || null;
        const content = e[t("locations.add.content")] || null;
        const businessInterruption =
          e[t("locations.add.businessInterruption")] || null;
        const propertyDamage = e[t("locations.add.propertyDamage")] || null;
        const limit = e[t("locations.add.limit")] || null;
        const deductible = e[t("locations.add.deductible")] || null;
        const other = e[t("locations.add.other")] || null;
        const total = e[t("locations.add.total")] || null;

        const isNumeric = (val: string | number) => !isNaN(Number(val));
        if (numberOfRisks && !isNumeric(numberOfRisks)) {
          errorData.push({
            row,
            message: t("importErrorMessage.numberOfRisksError")
          });
        }
        if (building && !isNumeric(building)) {
          errorData.push({
            row,
            message: t("importErrorMessage.buildingError")
          });
        }
        if (content && !isNumeric(content)) {
          errorData.push({
            row,
            message: t("importErrorMessage.contentError")
          });
        }
        if (businessInterruption && !isNumeric(businessInterruption)) {
          errorData.push({
            row,
            message: t("importErrorMessage.businessInterruptionError")
          });
        }
        if (propertyDamage && !isNumeric(propertyDamage)) {
          errorData.push({
            row,
            message: t("importErrorMessage.propertyDamageError")
          });
        }
        if (limit && !isNumeric(limit)) {
          errorData.push({ row, message: t("importErrorMessage.limitError") });
        }
        if (deductible && !isNumeric(deductible)) {
          errorData.push({
            row,
            message: t("importErrorMessage.deductibleError")
          });
        }
        if (other && !isNumeric(other)) {
          errorData.push({ row, message: t("importErrorMessage.otherError") });
        }
        if (total && !isNumeric(total)) {
          errorData.push({ row, message: t("importErrorMessage.totalError") });
        }

        const occupancy = OCCUPANCY[occupancyLabel];

        return {
          id: new Date().getTime() + i + "",
          coordinateSource,
          coordinateSystem,
          country,
          countryISO3,
          county,
          code,
          latitude,
          longitude,
          name,
          number,
          place,
          province,
          street,
          zipCode,
          address,
          insuredName,
          insuredCode,
          inceptionDate,
          expirationDate,
          evaluationDate,
          // currency,
          perilsCovered,
          occupancy,
          numberOfRisks,
          building,
          content,
          businessInterruption,
          propertyDamage,
          limit,
          deductible,
          other,
          total
        };
      });

      let params = {
        // id: newUUID.toString(),
        name,
        currency,
        comment,
        items
      };

      if (errorData.length > 0) {
        dispatch({ type: AppAction.setErrorData, payload: errorData });
        setLoading(false);
        showErrorModal();
        return;
      }

      dispatch({ type: AppAction.setImportMultipleLocations, payload: params });
      dispatch({ type: AppAction.setEditModal, payload: true });
      setLoading(false);
      close();
    };

    reader.readAsArrayBuffer(file);
  };

  const showErrorModal = (): void => {
    dispatch({ type: AppAction.setErrorModal, payload: true });
  };

  const downloadTip = () => {
    dispatch({
      type: AppAction.addSnackBarParams,
      payload: {
        title: "downloadTitle",
        titleIconColor: "white",
        titleIcon: "error",
        message: "downloadText"
      }
    });
  };

  return (
    <Modal
      header={t("locations.modal.title")}
      opened={context.importModal}
      onClose={close}
    >
      <div className={styles.modal}>
        <input
          className={styles.uploadIpt}
          onClick={(e) => e.stopPropagation()}
          onChange={hanldeChangeUploadFile}
          type="file"
        />
        <div className={styles.import}>
          <div className={styles.upload}>
            <ExcelSvg className={styles.icon} />
            <div className={styles.text1}>{t("locations.modal.text1")}</div>
            <div className={styles.text2}>
              <div>{t("locations.modal.text2")}</div>
              &nbsp;
              <div style={{ color: "green" }}>{t("locations.modal.text3")}</div>
              &nbsp;
              <div>{t("locations.modal.text4")}</div>
              &nbsp;
            </div>
          </div>
          {loading && (
            <div
              className="loading loading-spinner"
              style={{ color: "#666" }}
            ></div>
          )}
          <div className={styles.hint}>{t("locations.modal.hint")}</div>
          <a
            className={styles.link}
            onClick={downloadTip}
            href={`${process.env.PUBLIC_URL}/template/import/${context.language === "en" ? "location-set-import-template.xlsx" : "地点集导入模版.xlsx"}?_=${new Date().getTime()}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("locations.modal.link")}
          </a>
        </div>
      </div>
      <div className={styles.buttons}>
        <Button onClick={close}>{t("buttons.cancel")}</Button>
      </div>
    </Modal>
  );
}

export default ImportModal;
